import axios from 'axios';
import Button from 'tailwind-components/Button/Button';
import endpoints from 'constants/endpoints';
import images from 'constants/images';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import useDebounce from 'hooks/useDebounce';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LogOut } from 'state/actions/Authentication';
import { showAlert } from 'state/actions/site';
import useLogin from 'views/Login/LoginForm/useLogin';
import allcompanyconfig from '../../../companyconfig.json';
import contryCodes from '../PhoneNumberInput/contryCodes';
import CodeVerifier from '../CodeVerification/CodeVerifier';
import VerificationCode from '../CodeVerification/VerificationCode';
import styles from './ResetMFA.module.scss';
import React from 'react';

const RESEND_COOLDOWN_SECONDS = 30;

const ResetMFA = ({ email, password }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [codeSent, setCodeSent] = useState(false);
  const [done, setDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [seconds, setSeconds] = useState(RESEND_COOLDOWN_SECONDS);
  const [codeValid, setCodeValid] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(Array(10).fill(''));
  const [dialCode, setDialCode] = useState('+1');
  const [addPhoneNumberCode, setAddPhoneNumberCode] = useState('');
  const [phoneAdded, setPhoneAdded] = useState(false);

  const fullPhoneE164 = `${dialCode}${phoneNumber.join('')}`;

  const {
    login,
    resolver,
    sendCodeToSMS,
    verificationId,
    setRecaptchaVerifier,
    setUserCredentials,
    userCredentials,
    verifyingCode
  } = useLogin();

  useEffect(() => {
    if (!resolver) return;
    setRecaptchaVerifier(
      new firebase.auth.RecaptchaVerifier('recaptcha-container', {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
          dispatch(
            showAlert({
              message: 'Recaptcha expired',
              code: 'error'
            })
          );
        }
      })
    );
  }, [resolver]);

  const dispatch = useDispatch();
  const interval = useRef();

  const sendCode = () => {
    setIsLoading(true);
    axios({
      method: 'POST',
      url: `${allcompanyconfig.firebaseConfig.awsApiBaseUrl}${endpoints.aws.sendMFAResetCode}`,
      data: JSON.stringify({ email }),
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => {
        setCodeSent(true);
        setSeconds(RESEND_COOLDOWN_SECONDS);
        setDone(false);
      })
      .catch(() => {
        dispatch(
          showAlert({ message: 'Could not send reset code', code: 'error' })
        );
      })
      .finally(() => setIsLoading(false));
  };

  const validateCode = () => {
    if (code.join('').length !== 6) return;
    setIsLoading(true);
    axios({
      method: 'POST',
      url: `${allcompanyconfig.firebaseConfig.awsApiBaseUrl}${endpoints.aws.validateMFAResetCode}`,
      data: JSON.stringify({ email, otpCode: code.join('') }),
      headers: { 'Content-Type': 'application/json' }
    })
      .then(res => {
        setCodeValid(true);
        setAddPhoneNumberCode(res.data.otpCode);
        dispatch(
          showAlert({ message: 'Email verified successfully', code: 'success' })
        );
      })
      .catch(() => {
        setCodeValid(false);
        dispatch(showAlert({ message: 'Invalid code', code: 'error' }));
      })
      .finally(() => setIsLoading(false));
  };

  const sendNewPhoneNumber = () => {
    setIsLoading(true);
    axios({
      method: 'POST',
      url: `${allcompanyconfig.firebaseConfig.awsApiBaseUrl}${endpoints.aws.addPhoneNumberToMfa}`,
      data: JSON.stringify({
        email,
        phoneNumber: fullPhoneE164,
        otpCode: addPhoneNumberCode
      }),
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => {
        setIsLoading(false);
        setPhoneAdded(true);
        // Logout user
        LogOut();
        // Login user
        login(password, email);
      })
      .catch(e => {
        console.error(e);
        setIsLoading(false);
        dispatch(
          showAlert({ message: 'Failed to add phone number', code: 'error' })
        );
      });
  };

  const debouncedValidateCode = useDebounce(code, 300);

  useEffect(() => {
    if (isLoading) return;
    validateCode();
  }, [debouncedValidateCode]);

  useEffect(() => {
    if (done) return;
    interval.current = setInterval(() => setSeconds(prev => prev - 1), 1000);
    return () => clearInterval(interval.current);
  }, [done]);

  useEffect(() => {
    if (seconds <= 0) {
      clearInterval(interval.current);
      setDone(true);
    }
  }, [seconds]);

  return (
    <div className={styles.login}>
      {/* Required by Firebase RecaptchaVerifier(containerId) — must exist before verifier is constructed */}
      <div style={{ display: 'none' }} aria-hidden />
      <div className={styles.formContainer}>
        {(!phoneAdded || !verificationId) && (
          <>
            <img src={images.brand.fullLogo} alt={"O'Neill Logo"} />
            <p className={styles.subTitle}>
              {codeValid && !phoneAdded
                ? 'Enter new Phone Number'
                : 'Reset MFA'}
            </p>
          </>
        )}

        {codeValid ? (
          <>
            {phoneAdded ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: '10px'
                }}
              >
                {verificationId ? (
                  <VerificationCode
                    resolver={resolver}
                    userCredentials={userCredentials}
                    verificationId={verificationId}
                    setVerificationId={() => {}}
                    phoneNumber={fullPhoneE164}
                    setUserCredentials={setUserCredentials}
                    reloadPortal
                    embedded
                  />
                ) : (
                  <>
                    <p className={styles.description}>
                      We will send a 6 digit code to your phone number.
                    </p>
                    <Button
                      id='recaptcha-container'
                      text={'Send Code'}
                      isLoading={verifyingCode}
                      isDisabled={verifyingCode}
                      disabled={phoneNumber.some(digit => digit.trim() === '')}
                      onClick={e => {
                        e.preventDefault();
                        sendCodeToSMS(fullPhoneE164);
                      }}
                    />
                  </>
                )}
              </div>
            ) : (
              <form
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: '10px',
                  width: '100%'
                }}
                onSubmit={e => {
                  e.preventDefault();
                  sendNewPhoneNumber();
                }}
              >
                <p className={styles.description}>
                  Select your country code, then enter your mobile number (no
                  leading zero).
                </p>
                <select
                  style={{ width: '100%', marginBottom: '0.25rem' }}
                  onChange={e => {
                    const newDialCode = e.target.value.split('^')[1];
                    setDialCode(newDialCode);
                  }}
                  defaultValue={'US^+1'}
                  aria-label='Country code'
                >
                  {contryCodes.map(({ code, name, dial_code }) => (
                    <option key={code} value={`${code}^${dial_code}`}>
                      {name} {dial_code}
                    </option>
                  ))}
                </select>
                <CodeVerifier
                  type='phone'
                  length={10}
                  code={phoneNumber}
                  setCode={setPhoneNumber}
                  isLoading={isLoading}
                />
                <Button
                  type='submit'
                  variant='primary'
                  isLoading={isLoading}
                  disabled={
                    isLoading || phoneNumber.some(digit => digit.trim() === '')
                  }
                  text='Add Phone Number'
                />
              </form>
            )}
          </>
        ) : (
          <>
            {codeSent ? (
              <>
                <p className={styles.description}>
                  Code sent to your email: <strong>{email}</strong>.
                </p>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  <div style={{ width: '300px' }}>
                    <CodeVerifier
                      length={6}
                      code={code}
                      setCode={setCode}
                      isLoading={isLoading}
                    />
                  </div>
                </div>
                <div className={styles.resendSection}>
                  <p className={styles.resendText}>
                    {done
                      ? "Didn't get the code? Request a new one."
                      : `You can request a new code in ${seconds} seconds.`}
                  </p>
                  <Button
                    variant='secondary'
                    isLoading={!done}
                    disabled={!done}
                    text='Resend Code'
                    onClick={e => {
                      e.preventDefault();
                      sendCode();
                    }}
                  />
                </div>
              </>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: '10px'
                }}
              >
                <p className={styles.description}>
                  We will send a code to your email: <strong>{email}</strong>{' '}
                  <br />
                  <br /> Input the code to reset your MFA phone number.
                </p>
                <Button
                  variant='primary'
                  isLoading={isLoading}
                  disabled={isLoading}
                  text='Send Code'
                  onClick={() => sendCode()}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ResetMFA.propTypes = {
  email: PropTypes.string,
  password: PropTypes.string,
  resolver: PropTypes.object
};

export default ResetMFA;
